<script lang="ts" setup>
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

const props = withDefaults(
  defineProps<{
    to?: RouteLocationRaw
    variant?: 'contained' | 'outlined' | 'text'
    size?: 'small'
    color?: 'black'
    type?: 'button' | 'submit' | 'reset'
    fullWidth?: boolean
    active?: boolean
    disabled?: boolean
    loading?: boolean
    class?: string
  }>(),
  {
    to: undefined,
    variant: 'contained',
    type: 'button',
    size: undefined,
    color: undefined,
    class: undefined,
  },
)

const classes = computed(() => ({
  'app-button': true,
  'text-button': true,
  [`app-button--${props.variant}`]: true,
  [`app-button--${props.size}`]: !!props.size,
  [`app-button--${props.color}`]: !!props.color,
  'app-button--active': props.active,
  'app-button--disabled': props.disabled,
  'app-button--full-width': props.fullWidth,
  'app-button--loading': props.loading,
  [`${props.class}`]: !!props.class,
}))

const componentType = computed(() => {
  if (props.to) return resolveComponent('NuxtLink')

  return 'button'
})

const componentProps = computed(() => {
  const baseProps = {
    class: classes.value,
    'aria-disabled': props.disabled,
  }

  if (props.to) {
    return {
      ...baseProps,
      to: props.to,
    }
  } else {
    return {
      ...baseProps,
      type: props.type,
      disabled: props.disabled,
    }
  }
})
</script>

<template>
  <component :is="componentType" v-bind="componentProps">
    <slot name="icon-start"></slot>

    <span
      v-if="$slots['icon-start-circle']"
      class="app-button__icon-start-circle"
    >
      <slot name="icon-start-circle"></slot>
    </span>

    <span class="app-button__text"><slot></slot></span>

    <slot name="icon-end"></slot>

    <span v-if="$slots['icon-end-circle']" class="app-button__icon-end-circle">
      <slot name="icon-end-circle"></slot>
    </span>

    <Transition name="fade">
      <AppSpinner v-if="loading" />
    </Transition>
  </component>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-button {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  text-decoration: none !important;
  @include transition('color, background-color, border-color');

  .app-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    font-size: 2em;
    margin-top: -0.5em;
    margin-left: -0.5em;
  }
}

.app-button--contained {
  color: $white;
  background-color: $red;
  border: 1px solid $red;

  &:hover,
  &:focus {
    background-color: $tint-red-hover;
    border-color: $tint-red-hover;
  }

  &.app-button--black {
    background-color: $black;
    border-color: $black;

    &:hover,
    &:focus {
      background-color: $gray-400;
      border-color: $gray-400;
    }
  }
}

.app-button--outlined {
  color: $black;
  border: 1px solid $black;

  &:hover,
  &:focus,
  &.app-button--active {
    color: $white;
    background-color: $black;
    border-color: $black;
  }
}

.app-button--contained,
.app-button--outlined {
  padding: 1rem 1.5rem;
  border-radius: 0.125rem;

  &:disabled,
  &[aria-disabled='true'] {
    color: $white;
    background-color: $gray-300;
    border-color: $gray-300;
    pointer-events: none;
  }

  &.app-button--small {
    padding-top: 0.8125rem;
    padding-bottom: 0.8125rem;
  }

  @include media-breakpoint-up(md) {
    padding: 1.25rem 1.5rem;
  }
}

.app-button--text {
  color: $black;
  font-size: 0.75rem;
  line-height: 1.66666667;
  letter-spacing: 0.08333333em;

  &:hover {
    color: $tint-red-hover;
  }
}

.app-button--full-width {
  display: flex;
  width: 100%;
}

.app-button__icon-start-circle,
.app-button__icon-end-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid currentColor;
  border-radius: 50%;

  svg {
    width: 1rem;
    height: 1rem;
  }
}

.app-button__icon-start-circle {
  margin-right: 0.75rem;
}

.app-button__icon-end-circle {
  margin-left: 0.75rem;
}

.app-button__text {
  @include transition('opacity');
}

.app-button--loading {
  pointer-events: none;

  .app-button__text {
    opacity: 0;
  }
}
</style>
